import type {
  NavigationGuardReturn,
  NavigationGuardWithThis,
  RouteLocationNormalized,
  RouteLocationRaw,
  RouteMeta,
  RouteRecordRaw,
} from 'vue-router'
import { useAppToast } from '@/composables/ui/app-toast'
import type { AxiosError, AxiosResponse } from 'axios'
import { HttpStatusCode, isAxiosError } from 'axios'
import type { CarrierBookingDetail, BookingPrefill } from '@apiTypes'
import {
  CarrierAuctionStatusEnum,
  ConsentTypesEnum,
} from '@apiTypes'
import i18n from '@/plugins/i18n'
import { useAuctionsStore } from '@/stores/auction'
import { useAuthStore } from '@/stores/auth'
import { useCarriersStore } from '@store/carrier'
import { useAddressesStore } from '@/stores/address'
import {
  clearAuthData,
  isUserLoggedIn,
  refreshTokenHelper,
  switchWorkspaceHelper,
} from '@/auth/utils'
import { publicPages } from '@/router/public-pages'
import { useTendersStore } from '@/stores/tenders'
import { useCarrierTendersStore } from '@/stores/carrier-tenders'
import { useTransportsStore } from '@store/transport'
import { usePrivacyStore } from '@store/privacy'
import api from '@api'
import { PermissionObject, PermissionType } from '@/types/permissions'
import { userHasPermissions } from '@/utils/permissions.utils'
import * as Sentry from '@sentry/vue'
import { carrierPortalPages } from '@/router/carrier-portal-pages'
import { useCarrierPortalStore } from '@store/carrier-portal'
import { CARRIER_PORTAL_TOKEN } from '@/utils/const'
import { carrierAccessRedirect, isCarrierBlockedError } from '@/composables/session/carrier-access'
import {
  BookingLinkPageKind,
  bookingLinkPageState,
  loadBookingLink,
  loadBookingLinkBooking,
} from '@/views/time-slots/composables/carrier-booking-link'
import { useCarrierContractsByEmailStore } from '@/stores/carrier-contracts-by-email'
import { useCarrierBookingsStore } from '@/stores/carrier-bookings'
import { useBookingsStore } from '@/stores/bookings'
import { useCatalogsStore } from '@store/catalogs'
import {
  isContractAccessEnabledForWorkspace,
  isTenderEnabledForWorkspace,
  isTimeSlotsEnabledForWorkspace,
} from '@/composables/session/module-quotas'
import { isTenderEditableDisplayStatus, tenderDisplayStatusLabel } from '@/views/tenders/utils/tender-display-status.utils'
import { getTenderModifyPermissionType } from '@/views/tenders/utils/tender-permissions.utils'
import { getAuctionModifyPermissionType } from '@/views/requests/utils/auction-permissions.utils'
import { firstApiErrorMessage } from '@/utils/form.utils'
import { getQueryIdParam } from '@/utils/router.utils'
import { bookingDeletionEditReason, bookingDetailTo, canCarrierEditBooking, canEditBooking } from '@/views/time-slots/utils/booking.utils'
import {
  CARRIER_BOOKING_LINK_EDIT_ROUTE_NAME,
  CARRIER_BOOKING_LINK_ROUTE_NAME,
  type CarrierAuctionHashes,
  CarrierHashBookingRouteName,
  carrierAuctionTo,
  carrierBookingLinkTo,
  carrierHashBookingEditTo,
  carrierHashBookingTo,
  carrierTransportAuctionHashes,
} from '@/utils/carrier.utils'
import { isNullOrUndefined, parsePositiveInt } from '@/utils/common.utils'
import { isTransportStatusProcessing } from '@/views/transports/utils/transport.utils'

type RouteGuard = NavigationGuardWithThis<undefined>
type RouteChangeGuard = (
  to: RouteLocationNormalized,
) => Promise<NavigationGuardReturn>
type CarrierBookingFetcher = () => Promise<AxiosResponse<CarrierBookingDetail>>
type CarrierBookingPrefillFetcher = (waypoint: number) => Promise<AxiosResponse<BookingPrefill>>

function makeQuotaGuard(isEnabled: () => boolean): () => NavigationGuardReturn {
  return () => {
    if (!isEnabled()) {
      useAppToast().warning(i18n.global.t('This module is not available for your workspace.'))

      return { name: 'request-list' }
    }

    return true
  }
}

function withQuotaGuard(
  isEnabled: () => boolean,
  existingGuard: (to: RouteLocationNormalized) => Promise<NavigationGuardReturn>,
): (to: RouteLocationNormalized) => Promise<NavigationGuardReturn> {
  const quotaGuard = makeQuotaGuard(isEnabled)

  return async (to: RouteLocationNormalized) => {
    const quotaResult = quotaGuard()
    if (quotaResult !== true) return quotaResult

    return existingGuard(to)
  }
}

const tenderQuotaGuard = makeQuotaGuard(isTenderEnabledForWorkspace)

const withTenderQuotaGuard = (
  existingGuard: (to: RouteLocationNormalized) => Promise<NavigationGuardReturn>,
) => withQuotaGuard(isTenderEnabledForWorkspace, existingGuard)

const contractAccessQuotaGuard = makeQuotaGuard(isContractAccessEnabledForWorkspace)

const withContractAccessQuotaGuard = (
  existingGuard: (to: RouteLocationNormalized) => Promise<NavigationGuardReturn>,
) => withQuotaGuard(isContractAccessEnabledForWorkspace, existingGuard)

const timeSlotsQuotaGuard = makeQuotaGuard(isTimeSlotsEnabledForWorkspace)

const withTimeSlotsQuotaGuard = (
  existingGuard: (to: RouteLocationNormalized) => Promise<NavigationGuardReturn>,
) => withQuotaGuard(isTimeSlotsEnabledForWorkspace, existingGuard)

type EntityViewGuardOptions<K extends keyof RouteMeta> = {
  metaKey: K
  fetch: (id: number) => Promise<NonNullable<RouteMeta[K]>>
  notFoundMessage: () => string
  redirect: RouteLocationRaw
}

function makeEntityViewGuard<K extends keyof RouteMeta>({
  metaKey,
  fetch,
  notFoundMessage,
  redirect,
}: EntityViewGuardOptions<K>): RouteChangeGuard {
  return async (to: RouteLocationNormalized) => {
    const id = +to.params.id
    if (!Number.isInteger(id) || id <= 0)
      return redirect

    try {
      to.meta[metaKey] = await fetch(id)

      return true
    }
    catch {
      useAppToast().error(notFoundMessage())

      return redirect
    }
  }
}

const auctionViewChangedGuard = makeEntityViewGuard({
  metaKey: 'auction',
  fetch: async id => (await useAuctionsStore().fetchAuction(id)).data,
  notFoundMessage: () => i18n.global.t(
    'Request does not exist in this workspace, or you do not have access to it',
  ),
  redirect: { name: 'request-list' },
})

const addressViewGuard = makeEntityViewGuard({
  metaKey: 'address',
  fetch: async id => (await useAddressesStore().fetchAddress(id)).data,
  notFoundMessage: () => i18n.global.t(
    'Address does not exist in this workspace, or you do not have access to it',
  ),
  redirect: { name: 'directory-list', query: { tab: 'addresses' } },
})

const transportViewChangedGuard = makeEntityViewGuard({
  metaKey: 'transport',
  fetch: async id => (await useTransportsStore().fetchTransport(id)).data,
  notFoundMessage: () => i18n.global.t(
    'Transport does not exist in this workspace, or you do not have access to it',
  ),
  redirect: { name: 'transport-list' },
})

const bookingViewGuard = makeEntityViewGuard({
  metaKey: 'bookingDetail',
  fetch: async id => (await useBookingsStore().fetchBookingDetail(id)).data,
  notFoundMessage: () => i18n.global.t(
    'Booking does not exist in this workspace, or you do not have access to it',
  ),
  redirect: { name: 'bookings' },
})

export const routeGuardMap: Record<string, RouteGuard> = {
  'request-create': auctionCreateGuard,
  'request-copy-id': auctionCreateGuard,
  'request-edit-id': auctionEditGuard,
  'request-view-id': auctionViewChangedGuard,
  'directory-address-id': addressViewGuard,
  'transport-view-id': transportViewChangedGuard,
  'transport-edit-id': transportEditGuard,
  'bookings': timeSlotsQuotaGuard,
  'bookings-create': withTimeSlotsQuotaGuard(bookingCreateGuard),
  'bookings-view-id': withTimeSlotsQuotaGuard(bookingViewGuard),
  'bookings-edit-id': withTimeSlotsQuotaGuard(bookingEditGuard),
  'contracts-tenders-edit-id': withTenderQuotaGuard(tenderEditGuard),
  'contracts-tenders-view-id': withTenderQuotaGuard(tenderViewGuard),
  'contracts-tenders-create': withTenderQuotaGuard(tenderCreateGuard),
  'contracts-tenders-copy-id': withTenderQuotaGuard(tenderCreateGuard),
  'contracts-list': contractAccessQuotaGuard,
  'contracts-contract-view-id': withContractAccessQuotaGuard(contractViewGuard),
  'tenders-list': tenderQuotaGuard,
  'carrier-chash-auction-hash': carrierAuctionGuard,
  'carrier-tender-hash': carrierTenderGuard,
  'carrier-contract-hash': carrierContractGuard,
  'carrier-booking-hash': carrierBookingGuard,
  'carrier-booking-edit-hash': carrierBookingEditGuard,
  [CarrierHashBookingRouteName.CREATE]: carrierHashBookingCreateGuard,
  [CarrierHashBookingRouteName.DETAIL]: carrierHashBookingGuard,
  [CarrierHashBookingRouteName.EDIT]: carrierHashBookingEditGuard,
  [CARRIER_BOOKING_LINK_ROUTE_NAME]: carrierBookingLinkGuard,
  [CARRIER_BOOKING_LINK_EDIT_ROUTE_NAME]: carrierBookingLinkEditGuard,
  'privacy-preferences-hash': carrierConsentsGuard,
  'login': loggedUserRedirectGuard,
  'register': loggedUserRedirectGuard,
  'carrier-login': loggedCarrierRedirectGuard,
  'carrier-blocked': carrierBlockedPageGuard,
}

export const routeChangedGuardMap: Record<string, RouteChangeGuard> = {
  'request-view-id': auctionViewChangedGuard,
  'directory-address-id': addressViewGuard,
  'transport-view-id': transportViewChangedGuard,
  'bookings-view-id': withTimeSlotsQuotaGuard(bookingViewGuard),
  'bookings-edit-id': withTimeSlotsQuotaGuard(bookingEditGuard),
  'contracts-tenders-edit-id': withTenderQuotaGuard(tenderEditGuard),
  'contracts-tenders-view-id': withTenderQuotaGuard(tenderViewGuard),
}

export function extendRouteWithGuard(route: RouteRecordRaw): RouteRecordRaw {
  const routeName = route.children?.[0]?.name?.toString() || ''
  const guards: RouteGuard[] = []

  if (!publicPages.includes(routeName)) guards.push(dataGuard)

  if (carrierPortalPages.includes(routeName)) {
    guards.push(carrierPortalGuard)
  }

  if (routeGuardMap[routeName]) guards.push(routeGuardMap[routeName])

  return { ...route, beforeEnter: guards }
}

function paramsEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b))
    return a.length === b.length && a.every((v, i) => v === b[i])

  return false
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

export async function checkRouteChangedGuard(to: RouteLocationNormalized, from: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  if (to.name !== from.name) return true

  if (paramsEqual(to.params.id, from.params.id)) return true

  const routeName = typeof to.name === 'string' ? to.name : ''
  const guard = routeChangedGuardMap[routeName]
  if (guard) return await guard(to)

  return true
}

/**
 * Preserve route meta on same-route navigations when only query or hash changed.
 * Route guards (e.g. tenderEditGuard) run only on beforeEnter, so query-only
 * updates get a new `to` without meta (e.g. tenderOverview), which can hide
 * the page content. Copy meta from `from` so state is preserved.
 */
export function preserveMetaOnSameRouteQueryChange(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
): void {
  if (to.name !== from.name) return

  const sameParams = JSON.stringify(to.params) === JSON.stringify(from.params)

  if (!sameParams) return
  if (Object.keys(from.meta).length === 0) return

  Object.assign(to.meta, from.meta)
}

async function dataGuard(): Promise<NavigationGuardReturn> {
  if (isUserLoggedIn()) {
    try {
      await fetchData()

      return true
    }
    catch (e: unknown) {
      if (isAxiosError(e))
        return await checkGuardDataError(e, undefined, 'login')

      return { name: 'error' }
    }
  }

  return { name: 'login' }
}

function auctionCreateGuard(): NavigationGuardReturn {
  return checkPermission(PermissionObject.Request, PermissionType.Create, 'request-list')
}

async function auctionEditGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const id = +to.params.id
  if (!Number.isFinite(id) || id <= 0) {
    return { name: 'request-list' }
  }

  const auctionsStore = useAuctionsStore()
  const toast = useAppToast()

  try {
    const auction = (await auctionsStore.fetchAuction(id)).data
    const permType = getAuctionModifyPermissionType(auction.status)
    const permResult = checkPermission(PermissionObject.Request, permType, 'request-list')
    if (permResult !== true) return permResult

    to.meta.auction = auction

    return true
  }
  catch {
    toast.error(i18n.global.t('Request does not exist in this workspace, or you do not have access to it'))

    return { name: 'request-list' }
  }
}

// A workspace-scoped list answers 200 with no rows for a foreign or deleted id, so an empty page is the
// same failure as a rejection.
async function bookingCreateGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const transportId = getQueryIdParam(to.query.transport)
  if (transportId === null) return

  const transportsStore = useTransportsStore()
  const reportLoadFailure = () => useAppToast().error(i18n.global.t('Could not load the transport to book'))

  try {
    const transport = (await transportsStore.fetchTransports({ id: transportId, pageSize: 1 })).data.results?.[0]
    if (isNullOrUndefined(transport)) {
      reportLoadFailure()

      return
    }

    if (!isTransportStatusProcessing(transport.status)) {
      useAppToast().error(i18n.global.t('Bookings are available only while the transport is processing.'))

      return { name: 'transport-view-id', params: { id: String(transportId) } }
    }

    to.meta.bookingInitialSelection = { transport, waypointId: getQueryIdParam(to.query.waypoint) }
  }
  catch {
    reportLoadFailure()
  }
}

async function bookingEditGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const viewResult = await bookingViewGuard(to)

  if (viewResult !== true)
    return viewResult

  const booking = to.meta.bookingDetail
  const bookingId = Number(to.params.id)
  const detailRoute = bookingDetailTo(bookingId)
  const toast = useAppToast()

  if (isNullOrUndefined(booking) || !canEditBooking(booking.status)) {
    toast.error(i18n.global.t('Booking cannot be edited in its current status.'))

    return detailRoute
  }

  const deletionReason = bookingDeletionEditReason(booking)

  if (!isNullOrUndefined(deletionReason)) {
    toast.error(deletionReason)

    return detailRoute
  }

  const transportId = booking.transport?.id

  if (isNullOrUndefined(transportId))
    return true

  try {
    const transport = (await useTransportsStore().fetchTransports({ id: transportId, pageSize: 1 })).data.results?.[0]

    if (isNullOrUndefined(transport)) {
      toast.error(i18n.global.t('Could not load the transport to book'))

      return detailRoute
    }

    to.meta.bookingEditTransport = transport

    return true
  }
  catch {
    toast.error(i18n.global.t('Could not load the transport to book'))

    return detailRoute
  }
}

function transportEditGuard(): NavigationGuardReturn {
  return checkPermission(
    PermissionObject.Transport,
    PermissionType.Update,
    'transport-list',
  )
}

export async function carrierAuctionGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const contactHash = to.params.chash as string
  const auctionHash = to.params.hash as string

  const auctionsStore = useAuctionsStore()

  try {
    await fetchDataForCarrierByHash(contactHash)
    to.meta.auctionByHash = await auctionsStore.fetchCarrierAuction(
      auctionHash,
      contactHash,
    )

    return true
  }
  catch (e: unknown) {
    console.error('Failed to fetch carrier auction:', e)

    return isCarrierBlockedError(e) ? { name: 'carrier-blocked' } : { name: 'all' }
  }
}

function toastTenderRouteLoadFailure(toast: ReturnType<typeof useAppToast>, error: unknown): void {
  if (isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
    const detailRaw = isRecord(error.response.data) ? error.response.data.detail : undefined
    const detail = typeof detailRaw === 'string' ? detailRaw.trim() : ''

    toast.error(detail.length > 0 ? detail : i18n.global.t('Tender not found.'))

    return
  }

  toast.error(i18n.global.t('Failed to load tender. Please try again.'))
}

async function tenderCreateGuard(): Promise<NavigationGuardReturn> {
  return checkPermission(PermissionObject.Tender, PermissionType.Create, 'tenders-list')
}

async function tenderEditGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const id = +to.params.id
  if (!Number.isFinite(id) || id <= 0) {
    return { name: 'tenders-list' }
  }

  const tendersStore = useTendersStore()
  const toast = useAppToast()

  try {
    const overview = await tendersStore.fetchOverview(id)
    const status = overview.tender.status

    if (!isTenderEditableDisplayStatus(status)) {
      toast.error(
        i18n.global.t('Tender cannot be edited because it is {status}.', {
          status: tenderDisplayStatusLabel(status, i18n.global.t),
        }),
      )

      return {
        name: 'contracts-tenders-view-id',
        params: { id: String(id) },
      }
    }

    const permType = getTenderModifyPermissionType(status)
    const permResult = checkPermission(PermissionObject.Tender, permType, 'tenders-list')
    if (permResult !== true) return permResult

    to.meta.tenderOverview = overview
    to.meta.tenderStatus = status

    return true
  }
  catch (error: unknown) {
    toastTenderRouteLoadFailure(toast, error)

    return { name: 'tenders-list' }
  }
}

/** Tender metadata is loaded by tenderViewGuard — price offers are fetched lazily by the tab composable. */
async function tenderViewGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const id = +to.params.id
  if (!Number.isFinite(id) || id <= 0) {
    return { name: 'tenders-list' }
  }

  const tendersStore = useTendersStore()
  const toast = useAppToast()

  try {
    const { data } = await tendersStore.fetchTender(id)

    to.meta.tenderDetail = data

    return true
  }
  catch (error: unknown) {
    toastTenderRouteLoadFailure(toast, error)

    return { name: 'tenders-list' }
  }
}

async function contractViewGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const id = +to.params.id
  const toast = useAppToast()

  if (!Number.isFinite(id) || id <= 0) {
    return { name: 'contracts-list' }
  }

  try {
    const { data } = await api.contracts.contractsRetrieve({ id })

    to.meta.contractDetail = data

    return true
  }
  catch (error: unknown) {
    if (isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound) {
      toast.error(i18n.global.t('Contract not found.'))

      return { name: 'contracts-list' }
    }

    toast.error(i18n.global.t('Failed to load contract. Please try again.'))

    return { name: 'contracts-list' }
  }
}

export async function carrierTenderGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const tenderHash = to.params.hash as string

  const carrierTendersStore = useCarrierTendersStore()

  try {
    const tender = (await carrierTendersStore.fetchTender(tenderHash)).data

    to.meta.tender = tender

    if (tender.contactHash) {
      await fetchDataForCarrierByHash(tender.contactHash)
    }

    return true
  }
  catch (e: unknown) {
    console.error('Failed to fetch carrier tender:', e)

    const toast = useAppToast()

    toast.error(i18n.global.t('Tender not found.'))

    return { name: 'carrier-request-list' }
  }
}

export async function carrierContractGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const contractHash = to.params.hash as string
  const contractsStore = useCarrierContractsByEmailStore()

  try {
    const { data: contract } = await contractsStore.fetchCarrierContract(contractHash)

    to.meta.contract = contract

    return true
  }
  catch (e: unknown) {
    console.error('Failed to fetch carrier contract:', e)

    const toast = useAppToast()

    toast.error(i18n.global.t('Contract not found.'))

    return { name: 'carrier-request-list' }
  }
}

export function carrierBookingGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  return redirectToCarrierHashBooking(to, carrierHashBookingTo)
}

export function carrierBookingEditGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  return redirectToCarrierHashBooking(to, carrierHashBookingEditTo)
}

/** Old portal booking URLs (bookmarks, email fallback links) open the booking under its personal auction link. */
async function redirectToCarrierHashBooking(
  to: RouteLocationNormalized,
  hashBookingRoute: (hashes: CarrierAuctionHashes, bookingHash: string) => RouteLocationRaw,
): Promise<NavigationGuardReturn> {
  const bookingList: RouteLocationRaw = { name: 'carrier-booking-list' }

  try {
    const { data: booking } = await useCarrierBookingsStore().fetchCarrierBooking(to.params.hash as string)
    const hashes = carrierTransportAuctionHashes(booking.transport)

    if (!isNullOrUndefined(hashes))
      return hashBookingRoute(hashes, booking.hash)

    useAppToast().error(i18n.global.t('Booking not found.'))

    return bookingList
  }
  catch (error: unknown) {
    return carrierBookingLoadFailure(error, bookingList)
  }
}

export function carrierHashBookingCreateGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  return withCarrierAuctionHashes(to, (hashes, auctionRoute) => {
    const waypoint = parsePositiveInt(String(to.params.waypoint))

    if (isNullOrUndefined(waypoint))
      return Promise.resolve(auctionRoute)

    return loadCarrierBookingPrefill(
      to,
      () => useCarrierBookingsStore().fetchCarrierBookingPrefillByHash(hashes, waypoint),
      i18n.global.t('This stop cannot be booked.'),
      auctionRoute,
    )
  })
}

export function carrierHashBookingGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const bookingHash = to.params.booking as string

  return withCarrierAuctionHashes(to, (hashes, auctionRoute) => loadCarrierBooking(
    to,
    () => useCarrierBookingsStore().fetchCarrierBookingByHash(hashes, bookingHash),
    auctionRoute,
  ))
}

export function carrierHashBookingEditGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const bookingHash = to.params.booking as string
  const store = useCarrierBookingsStore()

  return withCarrierAuctionHashes(to, (hashes, auctionRoute) => loadEditableCarrierBooking(to, {
    fetchBooking: () => store.fetchCarrierBookingByHash(hashes, bookingHash),
    fetchPrefill: waypoint => store.fetchCarrierBookingPrefillByHash(hashes, waypoint),
    detailRoute: carrierHashBookingTo(hashes, bookingHash),
    onFailure: auctionRoute,
  }))
}

export async function carrierBookingLinkGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const hash = to.params.hash as string

  try {
    const link = await loadBookingLink(hash)

    to.meta.bookingLink = link

    if (bookingLinkPageState(link).kind === BookingLinkPageKind.Booked)
      to.meta.booking = await loadBookingLinkBooking(hash) ?? undefined

    return true
  }
  catch (e: unknown) {
    return bookingLinkLoadFailure(e)
  }
}

/** The edit form of the booking made through a link: only while the shipper has not decided on it yet. */
export async function carrierBookingLinkEditGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const hash = to.params.hash as string
  const linkRoute = carrierBookingLinkTo(hash)

  try {
    const link = await loadBookingLink(hash)

    if (bookingLinkPageState(link).kind !== BookingLinkPageKind.Booked)
      return linkRoute

    const booking = await loadBookingLinkBooking(hash)

    if (isNullOrUndefined(booking))
      return linkRoute

    const blocked = carrierBookingEditBlocked(booking, linkRoute)

    if (!isNullOrUndefined(blocked))
      return blocked

    to.meta.bookingLink = link
    to.meta.booking = booking

    return true
  }
  catch (e: unknown) {
    return bookingLinkLoadFailure(e)
  }
}

function bookingLinkLoadFailure(error: unknown): NavigationGuardReturn {
  console.error('Failed to fetch booking link:', error)

  return carrierAccessRedirect(error) ?? { name: 'all' }
}

/** Loads the by-hash carrier (locale, blocked state) before a no-login booking page loads its data. */
async function withCarrierAuctionHashes(
  to: RouteLocationNormalized,
  load: (hashes: CarrierAuctionHashes, auctionRoute: RouteLocationRaw) => Promise<NavigationGuardReturn>,
): Promise<NavigationGuardReturn> {
  const hashes: CarrierAuctionHashes = {
    contactHash: to.params.chash as string,
    auctionHash: to.params.hash as string,
  }

  try {
    await fetchDataForCarrierByHash(hashes.contactHash)
  }
  catch (e: unknown) {
    console.error('Failed to fetch carrier by hash:', e)

    return isCarrierBlockedError(e) ? { name: 'carrier-blocked' } : { name: 'all' }
  }

  return load(hashes, carrierAuctionTo(hashes.contactHash, hashes.auctionHash))
}

async function loadCarrierBooking(
  to: RouteLocationNormalized,
  fetchBooking: CarrierBookingFetcher,
  onFailure: NavigationGuardReturn,
): Promise<NavigationGuardReturn> {
  try {
    const { data: booking } = await fetchBooking()

    to.meta.booking = booking

    return true
  }
  catch (error: unknown) {
    return carrierBookingLoadFailure(error, onFailure)
  }
}

function carrierBookingLoadFailure(error: unknown, onFailure: NavigationGuardReturn): NavigationGuardReturn {
  const redirect = carrierAccessRedirect(error)

  if (redirect)
    return redirect

  const toast = useAppToast()

  if (isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
    toast.error(i18n.global.t('Booking not found.'))
  else
    toast.error(i18n.global.t('Failed to load booking. Please try again.'))

  return onFailure
}

async function loadEditableCarrierBooking(
  to: RouteLocationNormalized,
  options: {
    fetchBooking: CarrierBookingFetcher
    fetchPrefill: CarrierBookingPrefillFetcher
    detailRoute: RouteLocationRaw
    onFailure: NavigationGuardReturn
  },
): Promise<NavigationGuardReturn> {
  const bookingResult = await loadCarrierBooking(to, options.fetchBooking, options.onFailure)

  if (bookingResult !== true)
    return bookingResult

  const booking = to.meta.booking

  if (isNullOrUndefined(booking))
    return options.detailRoute

  const blocked = carrierBookingEditBlocked(booking, options.detailRoute)

  if (!isNullOrUndefined(blocked))
    return blocked

  const waypointId = booking.waypoint?.id

  if (isNullOrUndefined(waypointId)) {
    useAppToast().error(i18n.global.t('This booking cannot be edited.'))

    return options.detailRoute
  }

  return loadCarrierBookingPrefill(
    to,
    () => options.fetchPrefill(waypointId),
    i18n.global.t('This booking cannot be edited.'),
    options.detailRoute,
  )
}

function carrierBookingEditBlocked(booking: CarrierBookingDetail, detailRoute: RouteLocationRaw): RouteLocationRaw | null {
  const toast = useAppToast()

  if (!canCarrierEditBooking(booking.status)) {
    toast.error(i18n.global.t('Booking cannot be edited in its current status.'))

    return detailRoute
  }

  const deletionReason = bookingDeletionEditReason(booking)

  if (!isNullOrUndefined(deletionReason)) {
    toast.error(deletionReason)

    return detailRoute
  }

  return null
}

async function loadCarrierBookingPrefill(
  to: RouteLocationNormalized,
  fetchPrefill: () => Promise<AxiosResponse<BookingPrefill>>,
  badRequestFallback: string,
  onFailure: NavigationGuardReturn,
): Promise<NavigationGuardReturn> {
  try {
    const { data: prefill } = await fetchPrefill()

    to.meta.bookingPrefill = prefill

    return true
  }
  catch (error: unknown) {
    const redirect = carrierAccessRedirect(error)

    if (redirect)
      return redirect

    const toast = useAppToast()

    if (isAxiosError(error) && error.response?.status === HttpStatusCode.BadRequest)
      toast.error(firstApiErrorMessage(error.response.data) ?? badRequestFallback)
    else if (isAxiosError(error) && error.response?.status === HttpStatusCode.NotFound)
      toast.error(i18n.global.t('Transport not found.'))
    else
      toast.error(i18n.global.t('Failed to load the booking form. Please try again.'))

    return onFailure
  }
}

export async function carrierConsentsGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const token = to.params.hash as string

  const privacyStore = usePrivacyStore()

  try {
    const consentsResponse = await privacyStore.fetchConsents(token, [
      ConsentTypesEnum.EMAIL,
    ])

    const termsResponse = await privacyStore.fetchTerms(token)

    to.meta.consents = consentsResponse.data
    to.meta.terms = termsResponse.data

    const contactHash = consentsResponse.data.contactHash || ''
    if (contactHash) await fetchDataForCarrierByHash(contactHash)

    return true
  }
  catch (e: unknown) {
    console.error('Failed to fetch privacy preferences:', e)

    return { name: 'all' }
  }
}

export async function carrierPortalGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const otpHash = localStorage.getItem(CARRIER_PORTAL_TOKEN) || ''
  if (!otpHash) return { name: 'carrier-login', query: { to: to.path } }

  const otpStore = useCarrierPortalStore()
  try {
    await otpStore.fetchOneTimePassword(otpHash)

    return true
  }
  catch (e: unknown) {
    if (isCarrierBlockedError(e)) return { name: 'carrier-blocked' }

    // Already in the app with expired OTP — stay on current page, session expiry dialog handles it.
    // On fresh page load (store empty), fall through to redirect to login.
    if (isAxiosError(e) && e.response?.data?.code?.includes('otp_expired') && otpStore.getOtpHash) {
      return false
    }

    console.error('Failed to fetch carrier one time password:', e)

    return { name: 'carrier-login', query: { to: to.path } }
  }
}

export async function carrierBlockedPageGuard(): Promise<NavigationGuardReturn> {
  const otpHash = localStorage.getItem(CARRIER_PORTAL_TOKEN) || ''

  // Blocked carriers arriving from a by-hash link have no portal session to verify against.
  if (!otpHash) return true

  const otpStore = useCarrierPortalStore()
  try {
    await otpStore.fetchOneTimePassword(otpHash)

    return {
      name: 'carrier-request-list',
      query: { carrierAuctionStatus: CarrierAuctionStatusEnum.ACTIVE },
    }
  }
  catch {
    return true // can't prove they're allowed in — show the blocked page
  }
}

export async function loggedCarrierRedirectGuard(): Promise<NavigationGuardReturn> {
  const otpHash = localStorage.getItem(CARRIER_PORTAL_TOKEN) || ''

  if (!otpHash) {
    return true
  }

  const otpStore = useCarrierPortalStore()
  try {
    await otpStore.fetchOneTimePassword(otpHash)

    return {
      name: 'carrier-request-list',
      query: { carrierAuctionStatus: CarrierAuctionStatusEnum.ACTIVE },
    }
  }
  catch (e: unknown) {
    return isCarrierBlockedError(e) ? { name: 'carrier-blocked' } : true
  }
}

export async function loggedUserRedirectGuard(): Promise<NavigationGuardReturn> {
  const authStore = useAuthStore()

  if (isUserLoggedIn()) {
    try {
      await authStore.fetchCurrentUser()

      return { name: 'request-list' }
    }
    catch (e: unknown) {
      if (isAxiosError(e)) return await checkGuardDataError(e, 'request-list')

      return { name: 'error' }
    }
  }

  return true
}

export async function switchWorkspaceGuard(to: RouteLocationNormalized): Promise<NavigationGuardReturn> {
  const workspaceId = to.params['workspaceId']
  if (!workspaceId) {
    return true
  }

  const toast = useAppToast()

  try {
    const response = await switchWorkspaceHelper(+workspaceId)

    useCatalogsStore().invalidateVehicleTypesCache()
    toast.info(
      i18n.global.t('Active workspace changed to {name}', {
        name: response.data.workspace.name,
      }),
    )
  }
  catch {
    toast.error(
      i18n.global.t('Workspace does not exist or you do not have access to it'),
    )
  }

  return to.fullPath.replace(`/w/${workspaceId}`, '')
}

async function checkGuardDataError(
  error: AxiosError,
  redirectName?: string,
  unauthorizedRedirectName?: string,
): Promise<NavigationGuardReturn> {
  if (error.response?.status === 401) {
    try {
      await refreshTokenHelper()
      await fetchData()

      if (redirectName) return { name: redirectName }

      return true
    }
    catch {
      // if there is error during token refresh we should redirect to login or register page based on user navigation
      clearAuthData()
      if (unauthorizedRedirectName) return { name: unauthorizedRedirectName }

      return true
    }
  }

  return { name: 'error' }
}

async function fetchData() {
  const authStore = useAuthStore()
  if (!authStore.currentUser) await authStore.fetchCurrentUser()

  if (authStore.currentUser)
    Sentry.setUser({
      id: authStore.currentUser.id,
      email: authStore.currentUser.email,
    })
}

async function fetchDataForCarrierByHash(hash: string) {
  const carrierStore = useCarriersStore()
  if (carrierStore.currentCarrierByHash?.hash !== hash)
    await carrierStore.fetchCurrentCarrierByHash(hash)

  Sentry.setUser({ email: carrierStore.currentCarrierByHash?.email })
}

function checkPermission(
  object: PermissionObject,
  type: PermissionType,
  redirect: string,
): NavigationGuardReturn {
  const authStore = useAuthStore()
  const toast = useAppToast()

  if (
    !authStore.currentUser
    || !userHasPermissions(authStore.currentUser, object, type)
  ) {
    toast.error(
      i18n.global.t('You do not have permission to access this object'),
    )

    return { name: redirect }
  }

  return true
}
